import React, { useContext } from 'react';
import { GameState } from '../../../gameTypes';
import ChampionCard from './ChampionCard/ChampionCard';
import AddChampion, { ChampionAddData } from './AddChampion';
import './GameGrid.css';
import { GameLog } from '../Logs/LogsDisplay';
// Import our custom WebSocketAdapter context
import SocketContext, { WebSocketAdapter } from '../../../../NewWebSocket/components/WebSocketAdapter';

interface GameGridProps {
  gameState: GameState | null;
  logsByChampion: Record<string, GameLog[]>;
  onCellClick: (row: number, col: number) => void;
  selectedCell: { row: number; col: number } | null;
  onChampionSelected: (championData: ChampionAddData) => void;
  onCancelSelection: () => void;
  onSelectTargetClick: (championId: string) => void;
  isSelectingTarget: boolean;
  selectingChampionId: string | null;
  onTargetSelected: (targetChampionId: string) => void;
}

const GameGrid: React.FC<GameGridProps> = ({
  gameState,
  logsByChampion,
  onCellClick,
  selectedCell,
  onChampionSelected,
  onSelectTargetClick,
  isSelectingTarget,
  selectingChampionId,
  onTargetSelected,
  onCancelSelection,
}) => {
  const socket = useContext(SocketContext);
  const grid = gameState?.grid || [];
  
  const handleGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  // Log grid dimensions for debugging
 // console.log('Rendering grid with dimensions:', grid.length, 'x', grid[0]?.length);

  const handleCellClickWrapper = (rowIndex: number, colIndex: number) => {
    const cell = grid[rowIndex][colIndex];
    const champion = cell.champion;
 //   console.log('Cell clicked at', rowIndex, colIndex, 'Champion:', champion?.name);

    if (isSelectingTarget && champion && champion.id !== selectingChampionId) {
      // Target selection mode and clicked on a different champion
      onTargetSelected(champion.id);
    } else if (!isSelectingTarget) {
      // Normal mode
      onCellClick(rowIndex, colIndex);
    }
  };

  // Cheap local split: separate a champion's pre-routed logs into regular vs combo.
  // O(M) where M = events for this champion, instead of O(N) for all events.
  const splitChampionLogs = (champLogs: GameLog[]): { regularLogs: GameLog[], comboLogs: GameLog[] } => {
    const regularLogs: GameLog[] = [];
    const comboLogs: GameLog[] = [];
    champLogs.forEach(log => {
      if (log.sequenceId && log.sequenceId.startsWith('combo-')) {
        comboLogs.push(log);
      } else {
        regularLogs.push(log);
      }
    });
    return { regularLogs, comboLogs };
  };
  
  // Add row function - adds an empty row after the specified rowIndex
  // Only allows addition if we have less than 2 rows
  const handleAddRow = (rowIndex: number) => {
    if (!gameState || !socket) return;
    
    // Check if we already have 2 rows (max limit)
    if (grid.length >= 2) {
      console.log('Cannot add row: Maximum of 2 rows reached');
      return;
    }
    
    console.log(`Requesting to add row after index ${rowIndex}, current grid size: ${grid.length}x${grid[0]?.length}`);
    
    socket.emit('add-grid-row', {
      position: rowIndex,  // This is the index of the row AFTER which we want to add the new row
      direction: 'below'
    });
    console.log('[GameGrid] Sent add-grid-row event to server', { position: rowIndex, direction: 'below' });
  };

  // Duplicate row function - copies the champions from the specified row to a new row below
  // Only allows duplication if we have less than 2 rows
  const handleDuplicateRow = (rowIndex: number) => {
    if (!gameState || !socket) return;
    
    // Check if we already have 2 rows (max limit)
    if (grid.length >= 2) {
      console.log('Cannot duplicate row: Maximum of 2 rows reached');
      return;
    }
    
    console.log(`Requesting to duplicate row at index ${rowIndex}, current grid size: ${grid.length}x${grid[0]?.length}`);
    
    socket.emit('duplicate-grid-row', {
      position: rowIndex
    });
    console.log('[GameGrid] Sent duplicate-grid-row event to server', { position: rowIndex });
  };

  // Render row separators with both add and duplicate buttons (max 2 rows)
  const renderRowSeparator = (rowIndex: number) => {
    const isMaxRows = grid.length >= 2;
    const addTooltipText = isMaxRows 
      ? "Grid is currently capped at 2 rows" 
      : "Add empty row here";
    const duplicateTooltipText = isMaxRows 
      ? "Grid is currently capped at 2 rows" 
      : "Duplicate this row";
    
    return (
      <div className="row-separator" key={`separator-${rowIndex}`}>
        <div className="row-add-buttons">
          <button 
            className={`row-add-button ${isMaxRows ? 'disabled' : ''}`}
            onClick={() => handleAddRow(rowIndex)}
            title={addTooltipText}
            disabled={isMaxRows}
          >
            +
          </button>
          <button 
            className={`row-add-button duplicate-button ${isMaxRows ? 'disabled' : ''}`}
            onClick={() => handleDuplicateRow(rowIndex)}
            title={duplicateTooltipText}
            disabled={isMaxRows}
          >
            ⎘
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className='game-grid-container'>
      {/* Custom Damage Calculator Header */}
      <div className="damage-calculator-header">
        <h2>Damage Calculator Sandbox</h2>
        <p className="calculator-instructions">
          Select a champion card, then select a champion and a target.<br />
          Adjust runes, items and skill order.<br />
          Use the Q, W, E, R, A buttons to input a custom combo.<br />
        </p>
      </div>
      
      <div className='game-grid' onClick={handleGridClick}>
        {grid.map((row, rowIndex) => {
        return (
          <React.Fragment key={`row-fragment-${rowIndex}`}>
            <div key={`row-${rowIndex}`} className='grid-row'>
              {row.map((cell: any, colIndex: number) => {
              const champion = cell.champion;
              const isSelectedCell =
                selectedCell &&
                selectedCell.row === rowIndex &&
                selectedCell.col === colIndex;

              // Determine team based on row index
              const teamClass = colIndex === 0 ? 'team-blue' : 'team-red';

              // Define isSelectableTarget and isUnselectableTarget
              const isSelectableTarget =
                isSelectingTarget && champion && champion.id !== selectingChampionId;

              const isUnselectableTarget =
                isSelectingTarget && champion && champion.id === selectingChampionId;

              const cellClasses = [
                'grid-cell',
                teamClass,
                isSelectedCell ? 'selected' : '',
                isSelectingTarget ? 'selecting-target' : '',
                isSelectableTarget ? 'selectable-target' : '',
                isUnselectableTarget ? 'unselectable-target' : '',
              ]
                .filter(Boolean)
                .join(' ');

              // Fetch target champion if champion exists
              let targetChampion = null;
              if (champion && champion.targetPosition) {
                const targetRow = champion.targetPosition.y;
                const targetCol = champion.targetPosition.x;

                // Ensure target position is within bounds
                if (
                  targetRow >= 0 &&
                  targetRow < grid.length &&
                  targetCol >= 0 &&
                  targetCol < grid[0].length
                ) {
                  targetChampion = grid[targetRow][targetCol]?.champion;
                }
              }

              // Split pre-routed logs for this champion (cheap — only this champion's events)
              const championLogs = champion
                ? splitChampionLogs(logsByChampion[champion.id] || [])
                : { regularLogs: [] as GameLog[], comboLogs: [] as GameLog[] };

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={cellClasses}
                  onClick={() => handleCellClickWrapper(rowIndex, colIndex)}
                >
                  {isSelectedCell && !champion ? (
                    <AddChampion onSelectChampion={onChampionSelected} />
                  ) : champion ? (
                    <ChampionCard
                      champion={champion}
                      regularLogs={championLogs.regularLogs}
                      comboLogs={championLogs.comboLogs}
                      targetChampion={targetChampion}
                      onSelectTargetClick={onSelectTargetClick}
                    />
                  ) : (
                    <div className='empty-cell'></div>
                  )}
                </div>
              );
            })}
          </div>
          
            {/* Render button to add row after this row */}
            {renderRowSeparator(rowIndex)}
          </React.Fragment>
        );
      })}
      </div>
    </div>
  );
};

export default GameGrid;
